<template>
  <div
    ref="trigger"
    class="culture-list-item">
    <div
      ref="hexImage"
      class="image-container">
      <Imager
        :image="data.culture_list_item_image"
        :native="true" />
    </div>
    <div class="culture-content-container">
      <span
        ref="outline"
        class="hex-outline"></span>
      <div class="content-wrapper">
        <div
          class="title h4"
          v-html="data.culture_list_item_title"></div>
        <div
          class="text editor-formatting"
          v-html="data.culture_list_item_text"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Imager from '@/components/utility/Imager.vue'
import { gsap, Linear } from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { ref, onMounted, onBeforeUnmount } from 'vue'

gsap.registerPlugin(ScrollTrigger)

defineProps(['data'])

let tl

const trigger = ref(null)
const outline = ref(null)
const hexImage = ref(null)

onMounted(() => {
  tl = gsap.timeline({
    scrollTrigger: {
      trigger: trigger.value,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
    },
  })

  tl.to(outline.value, {
    y: '25%',
    ease: Linear.easeNone,
  }).to(
    hexImage.value,
    {
      x: 0,
      ease: Linear.easeNone,
    },
    0
  )
})

onBeforeUnmount(() => {
  tl.kill()
})
</script>

<style lang="scss">
.culture-list-item {
  position: relative;
  display: flex;
  margin: 0 0 80px;

  @include mobile {
    flex-direction: column;
  }

  &:nth-child(3n + 1) {
    .culture-content-container {
      @include tablet-landscape(1) {
        justify-content: flex-end;
      }

      @include mobile {
        justify-content: flex-start;
      }
    }

    .image-container {
      width: 158px;
      height: 181px;
      margin-left: auto;
      margin-right: 20px;
      transform: translateX(-20%);

      @include tablet-landscape(1) {
        margin-left: 5%;
      }

      @include mobile {
        margin: 0 0 30px 50px;
      }
    }

    .hex-outline {
      @include mobile {
        position: absolute;
        top: -40px;
        right: 40px;
      }
    }
  }

  &:nth-child(3n + 2) {
    .image-container {
      order: 1;
      width: 221px;
      height: 254px;
      margin-left: auto;
      margin-right: 10%;
      transform: translateX(10%);

      @include tablet-landscape(1) {
        margin-right: 5%;
      }

      @include mobile {
        order: 0;
        margin-bottom: 30px;
      }
    }

    .culture-content-container {
      order: 0;

      @include mobile {
        order: 1;
      }
    }

    .hex-outline {
      @include mobile {
        position: absolute;
        top: -40px;
        left: 40px;
      }
    }
  }

  &:nth-child(3n + 3) {
    .image-container {
      width: 191px;
      height: 220px;
      margin-left: 5%;
      transform: translateX(-10%);

      @include mobile {
        margin-bottom: 30px;
      }
    }

    .culture-content-container {
      margin-left: auto;
      margin-right: 100px;

      @include mobile {
        margin: 0;
      }
    }
  }

  &:last-child {
    margin: 0;
  }

  .image-container {
    position: relative;
    overflow: hidden;
    clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);

    .imager {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover;
      }
    }
  }

  .culture-content-container {
    display: flex;

    @include tablet-landscape(1) {
      flex: 1;
    }

    .hex-outline {
      display: block;
      width: 79px;
      height: 90px;
      margin-right: 12px;
      background-image: url('@/assets/img/hex-outline-small.svg');
      background-repeat: no-repeat;
      background-size: contain;
    }
  }

  .content-wrapper {
    flex: 1;
    max-width: 440px;
    padding: 75px 0 0;

    @include mobile {
      padding: 0;
    }

    .text {
      margin: 30px 0 0;
    }
  }
}
</style>
